import { useEffect, useMemo, useState } from 'react';

import { ButtonV2, ChevronLeftIcon, IconButton, ModalV2 as Modal, toast } from '@utilities';
import type { Question, ScreenerQuestionBank } from '@api/instant_survey/types';

import {
  buildScreenerValueMap,
  DEFAULT_TITLE,
  getHierarchyPromptKeys,
  getInitialScreenerPromptValues,
  getScreenerPromptKeys,
  useScreenerQuestionBanks,
} from './utilities';
import BankView from './components/BankView';
import LibraryView from './components/LibraryView';
import PreviewView from './components/PreviewView';

import styles from './_index.module.scss';

interface CommonModalScreenerQuestionProps {
  onClose: () => void;
  usedScreenerBankIds?: ReadonlySet<number>;
}

interface CreateModalScreenerQuestionProps extends CommonModalScreenerQuestionProps {
  initialQuestion?: never;
  mode: 'create';
  onSelectQuestion: (question: Question) => Promise<void>;
  onUpdateQuestion?: never;
}

interface EditModalScreenerQuestionProps extends CommonModalScreenerQuestionProps {
  initialQuestion: Question;
  mode: 'edit';
  onSelectQuestion?: never;
  onUpdateQuestion: (question: Question) => Promise<void>;
}

type ModalScreenerQuestionProps = CreateModalScreenerQuestionProps | EditModalScreenerQuestionProps;

const ModalScreenerQuestion = ({
  initialQuestion,
  mode,
  onClose,
  onSelectQuestion,
  onUpdateQuestion,
  usedScreenerBankIds = new Set<number>(),
}: ModalScreenerQuestionProps) => {
  const isEditing = mode === 'edit';
  const {
    data: banks,
    isError: hasBanksError,
    isLoading: isLoadingBanks,
    refetch: refetchBanks,
  } = useScreenerQuestionBanks();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bank, setBank] = useState<null | ScreenerQuestionBank>(null);
  const [previewQuestion, setPreviewQuestion] = useState<null | Question>(
    isEditing ? initialQuestion : null
  );
  const [promptValues, setPromptValues] = useState<Record<string, string>>({});
  const [touchedPromptKeys, setTouchedPromptKeys] = useState<Set<string>>(() => new Set());

  const questionPromptKeys = useMemo(
    () => getScreenerPromptKeys(previewQuestion),
    [previewQuestion]
  );
  const hierarchyPromptKeys = useMemo(
    () => getHierarchyPromptKeys(previewQuestion),
    [previewQuestion]
  );
  const promptKeys = questionPromptKeys;
  const missingPromptKeys = useMemo(
    () => promptKeys.filter((promptKey) => !promptValues[promptKey]?.trim()),
    [promptKeys, promptValues]
  );
  const isSubmitDisabled = isSubmitting || missingPromptKeys.length > 0;

  const resetPromptState = (question: null | Question) => {
    const initialPromptValues = getInitialScreenerPromptValues(question);

    setPromptValues(initialPromptValues);
    setTouchedPromptKeys(new Set());
  };

  useEffect(() => {
    resetPromptState(previewQuestion);
  }, [previewQuestion]);

  const handleBack = () => {
    if (previewQuestion) {
      setPreviewQuestion(null);
      return;
    }

    setBank(null);
  };

  const handleSubmit = async () => {
    if (!previewQuestion || isSubmitDisabled) return;

    setIsSubmitting(true);
    try {
      const question = {
        ...previewQuestion,
        screenerValueMap: buildScreenerValueMap(promptKeys, promptValues),
      };

      if (mode === 'edit') {
        await onUpdateQuestion(question);
      } else {
        await onSelectQuestion(question);
      }
    } catch (error) {
      if (!isEditing) throw error;

      console.error('Error updating screener question', error);
      toast.error('Error updating question');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePromptChange = (promptKey: string, value: string) => {
    setPromptValues((currentPromptValues) => ({
      ...currentPromptValues,
      [promptKey]: value,
    }));
    setTouchedPromptKeys((currentTouchedPromptKeys) =>
      new Set(currentTouchedPromptKeys).add(promptKey)
    );
  };

  const title =
    previewQuestion?.screenerLabel ||
    (isEditing ? previewQuestion?.text : bank?.name) ||
    DEFAULT_TITLE;
  const isNestedView = Boolean(previewQuestion || bank);

  const actionButtons = previewQuestion
    ? [
        <ButtonV2
          disabled={isSubmitting}
          key="modal-cancel-button"
          onClick={onClose}
          shape="round"
          type="button"
          variant="default"
        >
          Cancel
        </ButtonV2>,
        <ButtonV2
          disabled={isSubmitDisabled}
          isLoading={isSubmitting}
          key="modal-confirm-button"
          onClick={handleSubmit}
          shape="round"
          type="button"
          variant="primary"
        >
          {isEditing ? 'Apply Changes' : 'Add to Survey'}
        </ButtonV2>,
      ]
    : [];
  const headerLeading =
    isNestedView && !isEditing ? (
      <IconButton
        aria-label="Back"
        className={styles['modal-screener-question-back']}
        onClick={handleBack}
        type="button"
      >
        <ChevronLeftIcon />
      </IconButton>
    ) : undefined;

  return (
    <Modal
      buttons={actionButtons}
      className={styles['modal-screener-question']}
      headerLeading={headerLeading}
      isActive
      isScrollable
      onClose={onClose}
      size="fullscreen"
      title={title}
    >
      {!bank && !previewQuestion && (
        <LibraryView
          banks={banks}
          isError={hasBanksError}
          isLoading={isLoadingBanks}
          onRetry={refetchBanks}
          onSelectBank={setBank}
          usedScreenerBankIds={usedScreenerBankIds}
        />
      )}
      {bank && !previewQuestion && <BankView bank={bank} onPreviewQuestion={setPreviewQuestion} />}
      {previewQuestion && (
        <>
          <PreviewView
            hierarchyPromptKeys={hierarchyPromptKeys}
            isEditing={isEditing}
            missingPromptKeys={missingPromptKeys}
            onPromptChange={handlePromptChange}
            onResetPrompts={() => resetPromptState(previewQuestion)}
            previewQuestion={previewQuestion}
            promptValues={promptValues}
            touchedPromptKeys={touchedPromptKeys}
          />
        </>
      )}
    </Modal>
  );
};

export default ModalScreenerQuestion;
